import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackCTAClick, trackDropOff } from '@/components/analytics/FlowTracker';

// Destinations that count as a conversion intent click.
const CTA_PATHS = ['/ny-checkout', '/elaftale', '/opladere', '/eltjek'];
const CHECKOUT_PATHS = ['/ny-checkout'];

const isCheckout = (path) => CHECKOUT_PATHS.some((p) => path.startsWith(p));

/**
 * Wires the funnel events that were never being recorded:
 *  - cta_click     : any click on a link/button leading into the checkout funnel
 *  - drop_off      : leaving a checkout page without being redirected to signup
 * checkout_start / step_complete are fired by the checkout page itself,
 * external_redirect by checkoutRedirect when the visitor leaves for signup.
 */
export default function FunnelTracker() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  // ─── CTA clicks (delegated, so every existing CTA is covered) ───
  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest('a[href], button, [role="button"]');
      if (!el) return;

      const tagged = el.closest('[data-cta]');
      const href = el.getAttribute?.('href') || '';
      const isFunnelLink = href.startsWith('/') && CTA_PATHS.some((p) => href.startsWith(p));
      if (!isFunnelLink && !tagged) return;

      const label =
        tagged?.getAttribute('data-cta') ||
        (el.innerText || el.getAttribute('aria-label') || 'cta').trim().slice(0, 60);

      trackCTAClick(label, href || window.location.pathname, {
        from_page: window.location.pathname,
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // ─── Drop-off: navigated away from the checkout without redirecting to signup ───
  useEffect(() => {
    const from = prevPath.current;
    const to = location.pathname;
    prevPath.current = to;
    if (from === to) return;
    if (!isCheckout(from) || isCheckout(to)) return;

    const redirected = sessionStorage.getItem('checkout_redirected') === '1';
    if (redirected) {
      sessionStorage.removeItem('checkout_redirected');
      return;
    }
    trackDropOff('ny_checkout', sessionStorage.getItem('checkout_last_step') || 'unknown', `left_to:${to}`);
  }, [location.pathname]);

  return null;
}